/**
 * The escape hatch for keys that genuinely cannot be known at compile time — and ONLY those.
 *
 * A handful of call sites build a key from backend data: an account status (`status.active`), a Zoom
 * reason code (`syncNow.reason.zoom_error:502`), a month number (`months.short.7`). The literal is not in
 * the source, so `t()` cannot check it, and the old workaround was `t(dynamicKey, { defaultValue })` —
 * which under i18next's non-strict mode makes the key `string` and disables the type gate for that call.
 * We run `strictKeyChecks: true` (see ./i18next.d.ts) precisely so that hatch is shut. This is the
 * replacement: narrow, explicit, and it still checks the key — just at runtime instead of compile time.
 *
 * `asTKey("status.", s)` validates the assembled key against the real resource tree and returns it typed
 * as a member of the key union, or `null` if it misses. Two things fall out of that:
 *
 *   - The PREFIX is compile-checked — but NOT by the mechanism you would first reach for, and the
 *     difference is the whole ballgame. The tempting story is "`asTKey("staus.", s)` resolves
 *     `Extract<TKey, "staus.${string}">` to `never`, and `t(never)` is an error". MEASURED: it is not.
 *     `never` is assignable to every type, so `t(key)` with `key: never` compiles CLEAN — `tsc --build`
 *     stays exit 0, the ternary below collapses to its falsy branch, and the call site renders the raw
 *     server token forever. A dead prefix is exactly as silent as the bug this module exists to kill.
 *     So the check is made explicit instead: `PrefixMustMatchAKey` (below) rejects a prefix that matches
 *     ZERO real keys, at the argument position, where it cannot be ignored. A typo in the static half
 *     cannot ship — and now that is enforced, not merely asserted in a comment.
 *   - A miss is a `null` the caller must handle, so an unmapped code renders the caller's fallback copy.
 *     It can no longer render the raw key at an operator — which is how "the portal is broken" happens.
 *
 * Do not widen this. If you find yourself reaching for it with a fully static key, use `t("that.key")`
 * directly — that is the case the compiler already covers.
 */
import type { ParseKeys } from "i18next";
import th from "./locales/th";

/** Every valid translation key, as a literal union (e.g. "nav.home" | "status.active" | …). */
export type TKey = ParseKeys;

/** The keys that begin with `P`. `never` if none do — see `PrefixMustMatchAKey` for why that matters. */
export type TKeyStartingWith<P extends string> = Extract<TKey, `${P}${string}`>;

/**
 * Rejects a prefix that no real key starts with.
 *
 * This is the guard that makes a typo'd prefix a BUILD failure. Without it `asTKey("staus.", s)` returns
 * `never | null` — i.e. `null` — the caller's `key ? t(key) : fallback` is statically pinned to the
 * fallback, and nothing anywhere complains. The naive `t(never)` reasoning does not save us: `never` is
 * assignable to everything, so it type-checks. The failure has to be raised HERE, on the argument.
 *
 * `[TKeyStartingWith<P>] extends [never]` — tuple-wrapped so the check is not distributive (a bare
 * `X extends never` over a union would distribute and never be true for a multi-member union).
 */
type PrefixMustMatchAKey<P extends string> = [TKeyStartingWith<P>] extends [never]
  ? {
      __error: "No translation key starts with this prefix — asTKey() would always return null. Typo?";
    }
  : unknown;

/**
 * COMPILE-TIME CANARY for th.ts's `as const`.
 *
 * Be precise about what `as const` does and does not do here, because the obvious guess is wrong (I
 * guessed it, then measured it):
 *
 *   - It is NOT what makes the KEY gate work. Key names come from the object's property names, which are
 *     literal with or without `as const`. Strip it and `t("worklsit.title")` still fails to compile.
 *   - It IS what keeps every leaf VALUE a literal ("เรียงตาม {{column}}" rather than `string`). i18next
 *     reads those literals to extract `{{…}}` placeholders and type the options bag. With `as const`,
 *     `t("a11y.sortedBy", {})` and `t("a11y.sortedBy", { colunm: x })` are both compile errors. (The one
 *     gap, measured: OMITTING the options bag entirely is still allowed, because the parameter itself is
 *     optional in i18next's signature. So this checks the vars you pass, not that you passed any.)
 *
 * And losing it is SILENT: removing `as const` from th.ts leaves `tsc --build` at exit 0 while both of
 * those checks quietly evaporate (measured: 2 errors → 0). So it gets a canary that actually fires —
 * assert a known-interpolated leaf is still a literal. If it ever widens, this line fails to compile,
 * `tsc --build` fails, and `build:cf` stops the deploy.
 */
type AssertLeavesAreLiterals = string extends typeof th.a11y.sortedBy
  ? "BROKEN: th.ts leaf values widened to `string` — it has lost its `as const`. t() has silently stopped checking {{interpolation}} variables. See i18n/i18next.d.ts."
  : true;

/** Exported so it is a used symbol (and so a test can point at it); its real job is to refuse to compile. */
export const COPY_LEAVES_ARE_LITERAL: AssertLeavesAreLiterals = true;

/** Depth-first flatten of the resource tree into dotted leaf keys — the runtime mirror of `TKey`. */
function collectKeys(node: Record<string, unknown>, prefix: string, out: Set<string>): void {
  for (const [k, v] of Object.entries(node)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (typeof v === "string") out.add(key);
    else if (v !== null && typeof v === "object") {
      collectKeys(v as Record<string, unknown>, key, out);
    }
  }
}

/**
 * Built from th.ts, the same object that produces `TKey` — so the runtime check and the compile-time type
 * can never drift apart. (th.ts is the source of truth; en.ts is held to its shape by `Copy<typeof th>`.)
 */
const ALL_KEYS: ReadonlySet<string> = (() => {
  const out = new Set<string>();
  collectKeys(th, "", out);
  return out;
})();

/**
 * Assemble `prefix + segment` and return it as a real translation key, or `null` if no such key exists.
 *
 * The single cast in this module lives here, and it is the narrow, guarded kind: the string has just been
 * proven to be a member of `ALL_KEYS`, and `ALL_KEYS` is generated from the very object that defines
 * `TKey`. Callers get a checked key or a null they must deal with — never an unchecked `string`.
 *
 * The cast goes through `unknown` (not a widen — the runtime guard above is what actually proves it;
 * this only changes what the COMPILER checks at the cast site). Past ~1000 leaf keys, `TKey` is wide
 * enough that `Extract<TKey, ...>` at a generic `P` blows TS2590 ("union type too complex to represent")
 * on the direct cast. `as unknown as X` needs no structural comparison (`unknown` is bidirectionally
 * assignable), so it can't hit that ceiling — the return type callers see is unchanged.
 *
 * @param prefix  The static half, INCLUDING its joiner: `"status."`, `"customer.invoices.source_"`.
 *                Must be a prefix of at least one real key — `PrefixMustMatchAKey` fails the build
 *                otherwise, so a prefix orphaned by a th.ts reshuffle cannot silently degrade to `null`.
 * @param segment The runtime half (backend enum, month number, …).
 */
export function asTKey<P extends string>(
  prefix: P & PrefixMustMatchAKey<P>,
  segment: string | number,
): TKeyStartingWith<P> | null {
  const candidate = `${prefix}${segment}`;
  const found: string | null = ALL_KEYS.has(candidate) ? candidate : null;
  return found as unknown as TKeyStartingWith<P> | null;
}
